<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-1">
      <div class="text-dark font-size-18 font-weight-bold">
        Class-Wise Fee Collection & Outstanding Dues
      </div>
      <div class="small text-muted" v-if="selectedSession">
        Session: <b>{{ selectedSession.name }}</b>
      </div>
    </div>
    <div class="text-gray-6 mb-3">Comparison of Paid vs Outstanding dues across all classes</div>
    <div class="chart-container" style="position: relative; height: 350px; width: 100%;">
      <canvas ref="myChart"></canvas>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import ChartJSPluginDatalabels from 'chartjs-plugin-datalabels'

function formatCompactIndian(num) {
  if (!num || isNaN(num)) return '0'
  if (num >= 10000000) {
    const cr = num / 10000000
    return (cr % 1 === 0 ? cr : cr.toFixed(2)) + 'Cr'
  }
  if (num >= 100000) {
    const lakh = num / 100000
    return (lakh % 1 === 0 ? lakh : lakh.toFixed(2)) + 'L'
  }
  if (num >= 1000) {
    const k = num / 1000
    return (k % 1 === 0 ? k : k.toFixed(1)) + 'k'
  }
  return num.toString()
}

export default {
  name: 'ClassWiseDueChart',
  props: {
    reportData: Object,
    selectedSession: Object,
  },
  data: function () {
    return {
      chartInstance: null,
    }
  },
  watch: {
    reportData: {
      immediate: true,
      handler(reportDataLocal) {
        if (!reportDataLocal || !reportDataLocal.class_wise_data) return
        this.$nextTick(() => {
          this.renderChart(reportDataLocal.class_wise_data)
        })
      },
    },
  },
  methods: {
    formatCompactIndian,
    renderChart(classWiseReportData) {
      if (this.chartInstance) {
        this.chartInstance.destroy()
      }
      if (!this.$refs.myChart || !classWiseReportData || !classWiseReportData.length) return

      const classNames = classWiseReportData.map((repData) => repData.school_class ? repData.school_class.name : 'Unknown')
      const paidData = classWiseReportData.map((repData) => repData.classwise_total_paid_amount || 0)
      const unpaidData = classWiseReportData.map((repData) => repData.classwise_total_unpaid_amount || 0)

      this.chartInstance = new Chart(this.$refs.myChart, {
        type: 'bar',
        data: {
          labels: classNames,
          datasets: [
            {
              label: 'Paid',
              backgroundColor: '#52c41a',
              hoverBackgroundColor: '#73d13d',
              data: paidData,
              maxBarThickness: 40,
              minBarLength: 8, // Guarantees small amounts (e.g. ₹3,000 / ₹5,000) remain visually visible!
              datalabels: {
                display: false,
              },
            },
            {
              label: 'Due',
              backgroundColor: '#ff4d4f',
              hoverBackgroundColor: '#ff7875',
              data: unpaidData,
              maxBarThickness: 40,
              minBarLength: 8, // Guarantees small amounts (e.g. ₹3,000 / ₹5,000) remain visually visible!
              datalabels: {
                display: false,
              },
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'top',
            align: 'end',
            labels: {
              usePointStyle: true,
              fontSize: 12,
            },
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (tooltipItem, data) => {
                const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || ''
                const val = tooltipItem.yLabel || 0
                return `${datasetLabel}: ₹${val.toLocaleString('en-IN')}`
              },
              footer: (tooltipItems) => {
                let total = 0
                let paid = 0
                tooltipItems.forEach((item) => {
                  total += item.yLabel || 0
                  if (item.datasetIndex === 0) paid = item.yLabel || 0
                })
                const pct = total > 0 ? ((paid / total) * 100).toFixed(1) : 0
                return `Total: ₹${total.toLocaleString('en-IN')} (${pct}% Paid)`
              },
            },
          },
          scales: {
            xAxes: [
              {
                stacked: true,
                gridLines: { display: false },
                ticks: {
                  fontSize: 11,
                  fontColor: '#555',
                },
              },
            ],
            yAxes: [
              {
                stacked: true,
                gridLines: { color: '#f0f0f0' },
                ticks: {
                  beginAtZero: true,
                  callback: (value) => '₹' + formatCompactIndian(value),
                  fontSize: 11,
                  fontColor: '#888',
                },
              },
            ],
          },
        },
        plugins: [ChartJSPluginDatalabels],
      })
    },
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
    }
  },
}
</script>
